import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { useShopifyProducts } from '@/hooks/useShopifyProducts';
import { formatPrice, type ShopifyProduct } from '@/lib/shopify';
import { useCartStore, type CartItem } from '@/stores/cartStore';
import { trackAddToCart } from '@/lib/crm/track';
import { dlViewItemList, shopifyNodeToItem } from '@/lib/dataLayer';
import { reviewsService } from '@/services/reviews/reviewsService';
import type { ReviewsSummary } from '@/types/reviews';
import { cn } from '@/lib/utils';

/** All storefront products indexed by handle (shares the react-query cache with /loja). */
export function useProductsByHandle() {
  const { data, isLoading } = useShopifyProducts(50);
  const map = useMemo(() => {
    const m = new Map<string, ShopifyProduct>();
    data?.forEach((p) => m.set(p.node.handle, p));
    return m;
  }, [data]);
  return { map, isLoading };
}

/** Live Konfidency summary — same reviewsService source as HomeReviews. */
export function useReviewsSummary() {
  const [summary, setSummary] = useState<ReviewsSummary | null>(null);
  useEffect(() => {
    let active = true;
    reviewsService.getReviewsSummary().then((s) => active && setSummary(s)).catch(() => {});
    return () => { active = false; };
  }, []);
  return summary;
}

export const RatingLine = ({ className }: { className?: string }) => {
  const summary = useReviewsSummary();
  if (!summary || !summary.totalReviews) return null;
  return (
    <div className={cn('flex items-center justify-center gap-2 text-sm', className)}>
      <span className="flex" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="w-4 h-4 fill-v2-maos text-v2-maos" />
        ))}
      </span>
      <span className="font-semibold">{summary.averageRating.toFixed(1).replace('.', ',')}</span>
      <span className="opacity-75">· {summary.totalReviews} avaliações verificadas</span>
    </div>
  );
};

/** Same add-to-cart flow as ShopifyProductCard: cartStore.addItem (fires dataLayer/analytics/TikTok) + CRM trackAddToCart. */
export function useAddToCart() {
  const addItem = useCartStore((s) => s.addItem);
  return (product: ShopifyProduct | undefined) => {
    const variant = product?.node.variants.edges[0]?.node;
    if (!product || !variant || variant.availableForSale === false) return;
    const cartItem: CartItem = {
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    };
    try {
      addItem(cartItem);
    } catch (err) {
      console.error('[ATC] addItem failed:', err);
      toast.error('Não foi possível adicionar ao carrinho. Tente novamente.', { position: 'top-center' });
      return;
    }
    try {
      trackAddToCart((variant as any).sku || product.node.id, 1, parseFloat(variant.price.amount), product.node.title);
    } catch (e) {
      console.error('[CRM Track] add_to_cart error:', e);
    }
    toast.success(`${product.node.title} adicionado ao carrinho!`, { position: 'top-center' });
  };
}

/** view_item_list, same shape as VitrineBestSellers. Fires once per list. */
export function useViewItemList(listName: string, products: (ShopifyProduct | undefined)[]) {
  const fired = useRef(false);
  const ready = products.filter(Boolean) as ShopifyProduct[];
  useEffect(() => {
    if (fired.current || ready.length === 0 || ready.length < products.length) return;
    fired.current = true;
    dlViewItemList(
      listName,
      ready.map((p, idx) =>
        shopifyNodeToItem(p.node, parseFloat(p.node.priceRange.minVariantPrice.amount), 1, listName, idx),
      ),
    );
  }, [ready.length, products.length, listName]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function priceInfo(product: ShopifyProduct | undefined) {
  if (!product) return null;
  const price = product.node.priceRange.minVariantPrice;
  const current = parseFloat(price.amount);
  const compare = product.node.variants.edges[0]?.node.compareAtPrice;
  const compareAmount = compare ? parseFloat(compare.amount) : 0;
  const hasCompare = compareAmount > current;
  return {
    current,
    compareValue: hasCompare ? compareAmount : null,
    price: formatPrice(price.amount, price.currencyCode),
    compare: hasCompare ? formatPrice(compare!.amount, compare!.currencyCode) : null,
    discountPct: hasCompare ? Math.round((1 - current / compareAmount) * 100) : 0,
    savings: hasCompare ? formatPrice((compareAmount - current).toFixed(2), price.currencyCode) : null,
  };
}

export const PriceBlock = ({ product, className }: { product?: ShopifyProduct; className?: string }) => {
  const info = priceInfo(product);
  if (!info) return <div className={cn('h-12 w-32 rounded bg-v2-ink/5 animate-pulse', className)} />;
  return (
    <div className={className}>
      <div className="flex items-baseline gap-2 flex-wrap">
        {info.compare && <span className="text-sm line-through text-v2-muted">{info.compare}</span>}
        <span className="text-xl font-semibold">{info.price}</span>
      </div>
    </div>
  );
};

/** Reveal-on-scroll wrapper (IntersectionObserver; reduced motion = no animation). */
export const Reveal = ({ children, className, as: Tag = 'div', id }: { children: ReactNode; className?: string; as?: any; id?: string }) => {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} id={id} className={cn('reveal-section', shown && 'revealed', className)}>
      {children}
    </Tag>
  );
};

export const Eyebrow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn('text-sm font-semibold uppercase text-v2-blue mb-3', className)}>{children}</p>
);

export const H2 = ({ children, className }: { children: ReactNode; className?: string }) => (
  <h2 className={cn('font-narrow text-[2rem] leading-[1.05] md:text-5xl font-semibold text-v2-ink tracking-tight', className)}>{children}</h2>
);
